import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { ReactSVG } from "react-svg";
import i18n from "../../i18n/i18n.js";
import PageHeader from '../PageHeader/PageHeader.js';
import SlideInContent from '../AnimatedElements/SlideInContent.js';
import { Utils } from '../../Utils.js';
import RangeSlider from 'react-range-slider-input';

import 'react-range-slider-input/dist/style.css';
import './ProductsFilters.css';

const FILTER_BLOCK_STATE_PREFIX = 'products_filters_open_state';
const FILTER_BLOCK_STATE_TTL = 1000 * 60 * 60 * 24; // 24 óra

const getItemIdentifier = (item) => {
    if (!item) {
        return '';
    }

    return (
        item.url
        || item.product_parameter
        || item.value
        || item.id
        || item.key
        || item.product_parameter_category
        || JSON.stringify(item)
    );
};

const FilterBlock = ({title,selectedCount = 0,isOpen = true,items = [],selectedItems = new Set(),onToggle,onItemClick,}) => (
    <div className={`filter-block${isOpen ? ' open' : ''}`}>
        <button type="button" className='filter-block__header' onClick={onToggle}>
            <div className='filter-block__title'>
                <span>{Utils.htmlDecode(title)?.replace(':', '')}</span>
                {selectedCount > 0 && <span className='filter-block__selected-count'>{selectedCount} ×</span>}
            </div>
            <span className='filter-block__chevron' aria-hidden='true'></span>
        </button>
        <div className='filter-block__body'>
            <div className='filter-block__chips'>
                {items.map((item) => {
                    const key = getItemIdentifier(item);
                    const label = item.label || item.product_parameter;
                    const count = item.item_count;
                    const isActive = selectedItems.has(key);

                    return (
                        <button
                            key={key}
                            type='button'
                            className={`filter-block__chip${isActive ? ' selected' : ''}`}
                            onClick={() => onItemClick?.(item)}
                        >
                            <span className='filter-block__chip-label'>{Utils.htmlDecode(label)?.replace(':', '')}</span>
                            {typeof count === 'number' ? <span className='filter-block__chip-count'>({count})</span> : null}
                        </button>
                    );
                })}
            </div>
        </div>
    </div>
);

const toFiniteNumber = (value) => {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
        return undefined;
    }

    return numeric;
};

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const normalizeItemsCollection = (items) => {
    if (Array.isArray(items)) {
        return items;
    }

    if (isPlainObject(items)) {
        return Object.values(items);
    }

    return [];
};

const getFieldNamesMap = (block) => {
    const mapCandidate = block?.settings?.fieldnames;

    if (!isPlainObject(mapCandidate)) {
        return null;
    }

    return Object.keys(mapCandidate).reduce((accumulator, key) => {
        const value = mapCandidate[key];

        if (typeof value === 'string' && value.length > 0) {
            accumulator[key] = value;
        }

        return accumulator;
    }, {});
};

const resolveFieldNameForItem = (fieldNamesMap, item) => {
    if (!isPlainObject(fieldNamesMap)) {
        return null;
    }

    const candidates = [
        item?.id,
        item?.key,
        item?.value,
        item?.url,
        item?.product_parameter,
        item?.product_parameter_category,
        getItemIdentifier(item),
    ];

    for (let index = 0; index < candidates.length; index += 1) {
        const candidate = candidates[index];

        if (typeof candidate !== 'string' && typeof candidate !== 'number') {
            continue;
        }

        const lookupKey = `${candidate}`;

        if (Object.prototype.hasOwnProperty.call(fieldNamesMap, lookupKey)) {
            const resolved = fieldNamesMap[lookupKey];

            if (typeof resolved === 'string' && resolved.length > 0) {
                return resolved;
            }
        }
    }

    return null;
};

const RangeSliderBlock = ({
    title,
    selectedMin,
    selectedMax,
    min = 0,
    max = 0,
    step = 1,
    currency,
    thousand_separator,
    decimal_separator,
    isOpen = true,
    disabled = false,
    onToggle,
    onRangeChange,
    onRangeCommit,
}) => {
    const resolvedMin = Number.isFinite(min) ? min : 0;
    const resolvedMax = Number.isFinite(max) && max > resolvedMin ? max : resolvedMin;
    const resolveStep = () => {
        if (step === 'any') {
            return 'any';
        }

        const numericStep = toFiniteNumber(step);

        if (numericStep !== undefined && numericStep > 0) {
            return numericStep;
        }

        return 1;
    };

    const clamp = (value, lower, upper) => Math.min(Math.max(value, lower), upper);

    const computeInitialRange = useCallback(() => {
        const lowerSource = Number.isFinite(selectedMin) ? selectedMin : resolvedMin;
        const upperSource = Number.isFinite(selectedMax) ? selectedMax : resolvedMax;
        const lower = clamp(lowerSource, resolvedMin, resolvedMax);
        const upper = clamp(Math.max(lower, upperSource), resolvedMin, resolvedMax);

        return [lower, upper];
    }, [selectedMin, selectedMax, resolvedMin, resolvedMax]);

    const [rangeValues, setRangeValues] = useState(() => computeInitialRange());
    const latestRangeRef = useRef(rangeValues);

    useEffect(() => {
        setRangeValues(computeInitialRange());
    }, [computeInitialRange]);

    useEffect(() => {
        latestRangeRef.current = rangeValues;
    }, [rangeValues]);

    const sliderStep = resolveStep();

    const fractionDigits = (() => {
        if (sliderStep === 'any') {
            return 2;
        }

        if (typeof sliderStep !== 'number') {
            return 0;
        }

        return Number.isInteger(sliderStep) ? 0 : Math.min(2, `${sliderStep}`.split('.')[1]?.length || 2);
    })();

    const formatValue = (value) => {
        if (!Number.isFinite(value)) {
            return '';
        }

        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
            return '';
        }

        const fixed = numericValue.toFixed(fractionDigits);
        const separator = typeof thousand_separator === 'string' ? thousand_separator : ' ';
        const decimalSeparator = typeof decimal_separator === 'string' ? decimal_separator : ',';
        const [integerPartRaw, fractionPartRaw = ''] = fixed.split('.');
        const sign = integerPartRaw.startsWith('-') ? '-' : '';
        const integerDigits = sign ? integerPartRaw.slice(1) : integerPartRaw;
        const groupedInteger = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
        const trimmedFraction = fractionPartRaw.replace(/0+$/, '');

        let formatted = `${sign}${groupedInteger}`;

        if (fractionDigits > 0 && trimmedFraction.length > 0) {
            formatted += `${decimalSeparator}${trimmedFraction}`;
        }

        return currency ? `${formatted} ${currency}` : formatted;
    };

    const handleHeaderClick = () => {
        if (typeof onToggle === 'function') {
            onToggle();
        }
    };

    const handleInput = (values) => {
        const [lower, upper] = values || [];
        const nextRange = [
            clamp(Number.isFinite(lower) ? lower : resolvedMin, resolvedMin, resolvedMax),
            clamp(Number.isFinite(upper) ? upper : resolvedMax, resolvedMin, resolvedMax),
        ];

        setRangeValues(nextRange);
        latestRangeRef.current = nextRange;

        if (typeof onRangeChange === 'function') {
            onRangeChange({ min: nextRange[0], max: nextRange[1] });
        }
    };

    const emitCommit = () => {
        const currentRange = latestRangeRef.current || rangeValues;

        if (typeof onRangeCommit === 'function') {
            onRangeCommit({ min: currentRange[0], max: currentRange[1] });
        }
    };

    const resolvedTitle = (title || i18n.t('productsFilterPriceRange', { defaultValue: 'Ár' })).replace(':', '');
    const summaryMin = formatValue(rangeValues[0]);
    const summaryMax = formatValue(rangeValues[1]);
    const summary = summaryMin && summaryMax ? `${summaryMin} - ${summaryMax}` : '';
    const isSliderDisabled = disabled || resolvedMin >= resolvedMax;

    return (
        <div className={`filter-block range-slider-block${isOpen ? ' open' : ''}`}>
            <button type='button' className='filter-block__header' onClick={handleHeaderClick}>
                <div className='filter-block__title'>
                    <span>{resolvedTitle}</span>
                    {summary ? <span className='filter-block__selected-count'>{summary}</span> : null}
                </div>
                <span className='filter-block__chevron' aria-hidden='true'></span>
            </button>
            <div className='filter-block__body range-slider-block__body'>
                <div className='range-slider-block__slider'>
                    <RangeSlider
                        value={rangeValues}
                        min={resolvedMin}
                        max={resolvedMax}
                        step={sliderStep}
                        disabled={isSliderDisabled}
                        onInput={handleInput}
                        onThumbDragEnd={emitCommit}
                        onRangeDragEnd={emitCommit}
                        ariaLabel={[i18n.t('productsFilterPriceRangeMinThumb', { defaultValue: 'Minimum price' }), i18n.t('productsFilterPriceRangeMaxThumb', { defaultValue: 'Maximum price' })]}
                    />
                </div>
                <div className='range-slider-block__values'>
                    <span className='range-slider-block__value range-slider-block__value--min'>{formatValue(rangeValues[0])}</span>
                    <span className='range-slider-block__value range-slider-block__value--max'>{formatValue(rangeValues[1])}</span>
                </div>
            </div>
        </div>
    );
};

const ProuctsFilters = forwardRef((props, ref) => {

    const [filterLoading,setFilterLoading] = useState({'loading':((props.filter_loading) ? props.filter_loading : 0)});
    const [show,setShow] = useState(!!props.isVisible);
    const filterData = (props.filterData) ? props.filterData : {};
    const productCount = typeof props.productCount === 'number' ? props.productCount : 0;
    const disableInitialAnimation = !!props.disableInitialAnimation;
    const animationObjectRef = useRef(null);
    const visibleTimeoutRef = useRef(null);
    const pendingHideRef = useRef(false);
    const notifyParentOnHideRef = useRef(false);
    const { onFiltersChange } = props;
    const lastReportedFiltersRef = useRef({ queryString: '', payloadHash: '', activeCount: null });
    const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
    const [disableFiltersWhileLoading,setDisableFiltersWhileLoading] = useState(false);
    const priceData = filterData?.prices;
    const priceBlockKey = 'filter_block_prices';
    const priceValues = priceData?.values || {};
    const priceSelectedValues = priceData?.selected_values || {};
    const priceStep = priceData?.step ?? priceValues?.step;
    const priceCurrency = priceData?.currency || priceData?.suffix;
    const priceThousandSeparator = priceData?.thousand_separator || priceData?.thousandSeparator;
    const priceDecimalSeparator = priceData?.decimal_separator || priceData?.decimalSeparator;
    const priceTitle = priceData?.label || priceData?.title || priceData?.settings?.label_txt;
    const priceMinId = priceData?.min_id || 'min_price';
    const priceMaxId = priceData?.max_id || 'max_price';

    const clampPriceRange = useCallback((range) => {
        if (!priceData) {
            return null;
        }

        const baseMinRaw = toFiniteNumber(priceValues?.min);
        const baseMaxRaw = toFiniteNumber(priceValues?.max);

        if (baseMinRaw === undefined && baseMaxRaw === undefined) {
            return null;
        }

        const baseMin = baseMinRaw !== undefined ? baseMinRaw : 0;
        const upperFallback = baseMaxRaw !== undefined ? baseMaxRaw : baseMin;
        const baseMax = upperFallback >= baseMin ? upperFallback : baseMin;

        if (!range || typeof range !== 'object') {
            return { min: baseMin, max: baseMax };
        }

        const candidateMin = toFiniteNumber(range.min);
        const candidateMax = toFiniteNumber(range.max);

        const resolvedMin = Math.min(Math.max(candidateMin ?? baseMin, baseMin), baseMax);
        const resolvedMax = Math.min(Math.max(candidateMax ?? baseMax, resolvedMin), baseMax);

        return {
            min: resolvedMin,
            max: resolvedMax,
        };
    }, [priceData, priceValues]);

    const defaultPriceRange = useMemo(() => clampPriceRange(priceValues), [clampPriceRange, priceValues]);
    const initialPriceRange = useMemo(() => clampPriceRange(priceSelectedValues) || defaultPriceRange, [clampPriceRange, priceSelectedValues, defaultPriceRange]);
    const [selectedPriceRange, setSelectedPriceRange] = useState(initialPriceRange);
    const sliderMinValue = defaultPriceRange?.min ?? toFiniteNumber(priceValues?.min) ?? 0;
    const sliderMaxValue = defaultPriceRange?.max ?? toFiniteNumber(priceValues?.max) ?? sliderMinValue;

    useEffect(() => {
        setSelectedPriceRange((prev) => {
            if (!initialPriceRange && !prev) {
                return prev;
            }

            if (initialPriceRange && prev
                && prev.min === initialPriceRange.min
                && prev.max === initialPriceRange.max) {
                return prev;
            }

            return initialPriceRange || prev;
        });
    }, [initialPriceRange]);

    const selectedPriceRangeRef = useRef(selectedPriceRange);

    useEffect(() => {
        selectedPriceRangeRef.current = selectedPriceRange;
    }, [selectedPriceRange]);

    const defaultStorageKey = typeof window !== 'undefined'
        ? `${FILTER_BLOCK_STATE_PREFIX}_${(window.location.pathname || 'default').replace(/[^a-z0-9]/gi, '_')}`
        : `${FILTER_BLOCK_STATE_PREFIX}_default`;
    const storageKey = props.filtersStateStorageKey || defaultStorageKey;

    const [blocksOpened,setBlocksOpened] = useState(() => {
        if (typeof window === 'undefined') {
            return {};
        }

        const stored = Utils.localSorageGetWithExpiry(storageKey);

        if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
            return stored;
        }

        return {};
    });

    const buildFilter = useCallback(() => {
        const blockState = {};
        const productParameters = filterData?.product_parameters;

        let searchParams = null;

        if (currentSearch && typeof URLSearchParams !== 'undefined') {
            searchParams = new URLSearchParams(currentSearch);
        }

        const registerBlockState = (stateKey, block, parameterKey) => {
            if (!block) {
                return;
            }

            const items = normalizeItemsCollection(block.items);
            const selected = new Set();
            const fieldNamesMap = getFieldNamesMap(block);
            const categoryKey = fieldNamesMap
                ? null
                : block?.settings?.product_parameter_source_txt
                    || block?.settings?.label_txt
                    || block?.settings?.label
                    || parameterKey;

            let queryValueSet = null;

            if (!fieldNamesMap && searchParams && categoryKey) {
                const rawValue = searchParams.get(categoryKey);

                if (rawValue && rawValue.length > 0) {
                    const values = rawValue
                        .split('|')
                        .map((val) => `${val}`.trim())
                        .filter((val) => val.length > 0);

                    if (values.length > 0) {
                        queryValueSet = new Set(values);
                    }
                }
            }

            items.forEach((item) => {
                if (!item) {
                    return;
                }

                const identifier = getItemIdentifier(item);

                if (!identifier) {
                    return;
                }

                const rawItemValue = item?.url
                    ?? item?.product_parameter
                    ?? item?.value
                    ?? item?.id
                    ?? item?.key;
                const normalizedItemValue = (typeof rawItemValue === 'number' || typeof rawItemValue === 'string')
                    ? `${rawItemValue}`.trim()
                    : '';

                let itemSelected = item?.selected === true || item?.active === true;

                if (!itemSelected) {
                    if (fieldNamesMap && searchParams) {
                        const fieldName = resolveFieldNameForItem(fieldNamesMap, item);

                        if (fieldName) {
                            const paramRaw = searchParams.get(fieldName);

                            if (paramRaw !== null) {
                                const normalizedParam = `${paramRaw}`.trim();

                                if (normalizedParam.length === 0
                                    || normalizedParam === normalizedItemValue
                                    || normalizedParam === `${item?.id ?? ''}`.trim()
                                    || normalizedParam === `${item?.url ?? ''}`.trim()
                                ) {
                                    itemSelected = true;
                                }
                            }
                        }
                    } else if (queryValueSet && normalizedItemValue.length > 0 && queryValueSet.has(normalizedItemValue)) {
                        itemSelected = true;
                    }
                }

                if (itemSelected) {
                    selected.add(identifier);
                }
            });

            blockState[stateKey] = selected;
        };

        if (productParameters && typeof productParameters === 'object') {
            Object.keys(productParameters).forEach((key) => {
                registerBlockState(`filter_block_${key}`, productParameters[key], key);
            });
        }

        if (filterData?.status_filters?.statuses) {
            registerBlockState('statuses', filterData.status_filters.statuses, 'statuses');
        }

        return blockState;
    }, [filterData, currentSearch]);

    const [selectedItemsByBlock, setSelectedItemsByBlock] = useState(() => buildFilter());
    const selectedItemsByBlockRef = useRef(selectedItemsByBlock);

    useEffect(() => {
        selectedItemsByBlockRef.current = selectedItemsByBlock;
    }, [selectedItemsByBlock]);


    const persistBlocksState = useCallback((state) => {
        if (typeof window === 'undefined') {
            return;
        }

        Utils.localSorageSetWithExpiry(storageKey, state, FILTER_BLOCK_STATE_TTL);

    }, [storageKey]);

    const clearVisibleTimeout = () => {
        if (visibleTimeoutRef.current) {
            clearTimeout(visibleTimeoutRef.current);
            visibleTimeoutRef.current = null;
        }
    };

    const showFilters = useCallback(() => {
        if (show) {
            animationObjectRef.current?.setVisible(true);
            pendingHideRef.current = false;
            notifyParentOnHideRef.current = false;
            return;
        }

        setShow(true);

        clearVisibleTimeout();

        pendingHideRef.current = false;
        notifyParentOnHideRef.current = false;

        visibleTimeoutRef.current = setTimeout(() => {
            animationObjectRef.current?.setVisible(true);
        }, 0);
    }, [show]);

    const hideFilters = useCallback((triggerParent = false) => {

        clearVisibleTimeout();

        if (!show && !pendingHideRef.current) {
            notifyParentOnHideRef.current = false;
            if (triggerParent && typeof props.hideFromParent==='function'){
                props.hideFromParent(0,true);
            }
            return;
        }

        pendingHideRef.current = true;
        notifyParentOnHideRef.current = notifyParentOnHideRef.current || triggerParent;

        if (animationObjectRef.current) {
            animationObjectRef.current.setVisible(false);
        }

    }, [props.hideFromParent, show]);

    useEffect(()=>{
        if (props.isVisible) {
            showFilters();
        } else {
            hideFilters(false);
        }
    },[props.isVisible, showFilters, hideFilters]);

    useEffect(()=>{
        return () => {
            clearVisibleTimeout();
        };
    },[]);

    const hide = () => {
        hideFilters(true);
    };

    const toggle_filter_block = useCallback((blockKey) => {
        setBlocksOpened((prev) => {
            const current = Object.prototype.hasOwnProperty.call(prev, blockKey) ? prev[blockKey] : true;
            const nextState = {
                ...prev,
                [blockKey]: !current,
            };

            persistBlocksState(nextState);

            return nextState;
        });
    }, [persistBlocksState]);

    const is_block_open = useCallback((blockKey) => {
        if (Object.prototype.hasOwnProperty.call(blocksOpened, blockKey)) {
            return !!blocksOpened[blockKey];
        }

        return true;
    }, [blocksOpened]);

    const emitFiltersChange = useCallback((stateByBlock, options = {}) => {
        if (typeof onFiltersChange !== 'function') {
            return;
        }

        const { force = false, reason = 'user', priceRange: priceRangeOverride } = options;

        const productParameters = filterData?.product_parameters || {};
        const blockEntries = Object.entries(stateByBlock || {});

        let activeFiltersCount = blockEntries.reduce((total, [blockKey, selectedSet]) => {
            if (blockKey === priceBlockKey || !selectedSet || typeof selectedSet.size !== 'number') {
                return total;
            }

            return total + selectedSet.size;
        }, 0);

        const normalizedSelections = {};

        const resolveBlockMeta = (blockKey) => {
            const normalizedKey = blockKey.startsWith('filter_block_')
                ? blockKey.replace('filter_block_', '')
                : blockKey;

            if (productParameters[normalizedKey]) {
                const block = productParameters[normalizedKey];
                const fieldNamesMap = getFieldNamesMap(block);
                const categoryKey = fieldNamesMap
                    ? null
                    : block?.settings?.product_parameter_source_txt
                        || block?.settings?.label_txt
                        || block?.settings?.label
                        || normalizedKey;

                return {
                    parameterKey: normalizedKey,
                    block,
                    categoryKey,
                };
            }

            if ((normalizedKey === 'statuses' || normalizedKey === 'status_filters')
                && filterData?.status_filters?.statuses
            ) {
                const block = filterData.status_filters.statuses;
                const fieldNamesMap = getFieldNamesMap(block);
                const categoryKey = fieldNamesMap
                    ? null
                    : block?.settings?.product_parameter_source_txt
                        || block?.settings?.label_txt
                        || block?.settings?.label
                        || normalizedKey;

                return {
                    parameterKey: 'statuses',
                    block,
                    categoryKey,
                };
            }

            return null;
        };

        blockEntries.forEach(([blockKey, selectedSet]) => {
            if (blockKey === priceBlockKey || !selectedSet || selectedSet.size === 0) {
                return;
            }

            const meta = resolveBlockMeta(blockKey);

            if (!meta) {
                return;
            }

            const items = normalizeItemsCollection(meta.block?.items);
            const selectedItems = items.filter((item) => selectedSet.has(getItemIdentifier(item)));

            if (selectedItems.length > 0) {
                normalizedSelections[meta.parameterKey] = {
                    block: meta.block,
                    items: selectedItems,
                    categoryKey: meta.categoryKey,
                };
            }
        });

        let priceSelectionPayload = null;
        let priceIsActive = false;

        if (priceData && defaultPriceRange) {
            const effectiveRange = priceRangeOverride || selectedPriceRange;
            const normalizedRange = clampPriceRange(effectiveRange);

            if (normalizedRange) {
                const { min: normalizedMin, max: normalizedMax } = normalizedRange;
                const defaultMin = defaultPriceRange.min;
                const defaultMax = defaultPriceRange.max;

                const minChanged = Number.isFinite(defaultMin) ? normalizedMin > defaultMin : false;
                const maxChanged = Number.isFinite(defaultMax) ? normalizedMax < defaultMax : false;

                priceIsActive = minChanged || maxChanged;

                priceSelectionPayload = {
                    min: normalizedMin,
                    max: normalizedMax,
                    minId: priceMinId,
                    maxId: priceMaxId,
                    isActive: priceIsActive,
                    defaults: {
                        min: defaultPriceRange.min,
                        max: defaultPriceRange.max,
                    },
                };
            }
        }

        if (priceIsActive && priceSelectionPayload) {
            activeFiltersCount += 1;
        }

        const queryObject = {};
        const querySegments = [];

        const usedQueryKeys = new Set();

        Object.entries(normalizedSelections).forEach(([parameterKey, value]) => {
            const fieldNamesMap = getFieldNamesMap(value.block);

            if (fieldNamesMap) {
                value.items.forEach((item) => {
                    const fieldName = resolveFieldNameForItem(fieldNamesMap, item);

                    if (!fieldName || usedQueryKeys.has(fieldName)) {
                        return;
                    }

                    const rawValue = item?.url
                        ?? item?.product_parameter
                        ?? item?.value
                        ?? item?.id
                        ?? item?.key
                        ?? '1';
                    const valueString = `${rawValue}`;

                    queryObject[fieldName] = valueString;
                    usedQueryKeys.add(fieldName);
                    querySegments.push(`${encodeURIComponent(fieldName)}=${encodeURIComponent(valueString)}`);
                });

                return;
            }

            const categoryKey = value.categoryKey
                || value.block?.settings?.product_parameter_source_txt
                || value.block?.settings?.label_txt
                || value.block?.settings?.label
                || parameterKey;

            if (!categoryKey || usedQueryKeys.has(categoryKey)) {
                return;
            }

            const values = value.items
                .map((item) => item?.url ?? item?.product_parameter)
                .filter((val) => val !== undefined && val !== null)
                .map((val) => `${val}`.trim())
                .filter((val) => val.length > 0);

            if (values.length === 0) {
                return;
            }

            const uniqueValues = Array.from(new Set(values));
            const valueString = uniqueValues.join('|');

            queryObject[categoryKey] = valueString;
            usedQueryKeys.add(categoryKey);
            querySegments.push(`${encodeURIComponent(categoryKey)}=${encodeURIComponent(valueString)}`);
        });

        if (priceSelectionPayload?.isActive) {
            const { min, max, minId, maxId } = priceSelectionPayload;

            if (minId) {
                queryObject[minId] = min;
                querySegments.push(`${encodeURIComponent(minId)}=${encodeURIComponent(min)}`);
            }

            if (maxId) {
                queryObject[maxId] = max;
                querySegments.push(`${encodeURIComponent(maxId)}=${encodeURIComponent(max)}`);
            }
        }

        const payload = {
            selected: normalizedSelections,
            price: priceSelectionPayload,
            queryObject,
            queryString: querySegments.join('&'),
            activeFiltersCount,
            trigger: reason,
        };

        const payloadHash = JSON.stringify(payload.queryObject);

        if (!force
            && lastReportedFiltersRef.current.queryString === payload.queryString
            && lastReportedFiltersRef.current.payloadHash === payloadHash
            && lastReportedFiltersRef.current.activeCount === activeFiltersCount
        ) {
            return;
        }

        lastReportedFiltersRef.current = {
            queryString: payload.queryString,
            payloadHash,
            activeCount: activeFiltersCount,
        };

        onFiltersChange(payload, activeFiltersCount);
    }, [onFiltersChange, filterData, priceData, defaultPriceRange, clampPriceRange, selectedPriceRange, priceMinId, priceMaxId]);


    const handlePriceRangeCommit = useCallback((range) => {
        const normalized = clampPriceRange(range);

        if (!normalized) {
            return;
        }

        const previous = selectedPriceRangeRef.current;

        setSelectedPriceRange((prev) => {
            if (prev && prev.min === normalized.min && prev.max === normalized.max) {
                return prev;
            }

            return normalized;
        });

        if (previous && previous.min === normalized.min && previous.max === normalized.max) {
            return;
        }

        selectedPriceRangeRef.current = normalized;
        setDisableFiltersWhileLoading(true);
        emitFiltersChange(selectedItemsByBlockRef.current, { priceRange: normalized });
    }, [clampPriceRange, emitFiltersChange]);

    const handleItemClick = (blockKey, item) => {
        const identifier = getItemIdentifier(item);

        setDisableFiltersWhileLoading(true);

        setSelectedItemsByBlock((prev) => {
            const currentSet = new Set(prev[blockKey] || []);

            if (currentSet.has(identifier)) {
                currentSet.delete(identifier);
            } else {
                currentSet.add(identifier);
            }

            const nextState = {
                ...prev,
                [blockKey]: currentSet,
            };

            selectedItemsByBlockRef.current = nextState;
            emitFiltersChange(nextState);

            return nextState;
        });
    };

    useEffect(() => {
        const nextState = buildFilter();
        setSelectedItemsByBlock(nextState);
        selectedItemsByBlockRef.current = nextState;

        emitFiltersChange(nextState, { force: true, reason: 'sync', priceRange: initialPriceRange });
    }, [filterData, buildFilter, emitFiltersChange, initialPriceRange]);

    const handleSlideOutComplete = useCallback(() => {
        if (pendingHideRef.current) {
            pendingHideRef.current = false;
            setShow(false);

            if (notifyParentOnHideRef.current && typeof props.hideFromParent==='function'){
                notifyParentOnHideRef.current = false;
                props.hideFromParent(0,true);
            } else {
                notifyParentOnHideRef.current = false;
            }
        } else {
            notifyParentOnHideRef.current = false;
        }
    }, [props.hideFromParent]);

    useImperativeHandle(ref, () => ({
        show: showFilters,
        hide: () => hideFilters(false),
        toggle: () => (show ? hideFilters(false) : showFilters()),
        is_visible: () => show
    }), [show, showFilters, hideFilters]);

    if(!show) return null;

    return <SlideInContent
        ref={animationObjectRef}
        className={'container floating_container products_list_filters'}
        onHidden={handleSlideOutComplete}
        defaultVisible={disableInitialAnimation}
    >
        <div className='floating_container_inner'>
            <div className='floating_container_header'>
                <PageHeader parent_ref={{hide}} callback={hide} title={i18n.t('productsFilterTitle')}></PageHeader>
            </div>
            <div className='floating_container_content'>
                {disableFiltersWhileLoading ? <div className='loader_cont'><div className='loader'><ReactSVG src='/assets/images/loading.svg'></ReactSVG></div></div> : ''}
                <div className='filter_blocks_cont'>

                    {filterData.status_filters?.statuses?.items?.length > 0 ? <FilterBlock
                        key="statuses"
                        title={i18n.t('productListFiltersStatuses')}
                        selectedCount={selectedItemsByBlock.statuses?.size || 0}
                        items={filterData.status_filters.statuses.items}
                        selectedItems={selectedItemsByBlock.statuses || new Set()}
                        isOpen={is_block_open("statuses")}
                        onToggle={() => toggle_filter_block("statuses")}
                        onItemClick={(item) => handleItemClick("statuses", item)}
                    /> : ''}

                    {priceData ? (
                        <RangeSliderBlock
                            title={priceTitle}
                            selectedMin={selectedPriceRange?.min ?? defaultPriceRange?.min}
                            selectedMax={selectedPriceRange?.max ?? defaultPriceRange?.max}
                            min={sliderMinValue}
                            max={sliderMaxValue}
                            step={priceStep}
                            currency={priceCurrency}
                            thousand_separator={priceThousandSeparator}
                            decimal_separator={priceDecimalSeparator}
                            isOpen={is_block_open(priceBlockKey)}
                            onToggle={() => toggle_filter_block(priceBlockKey)}
                            onRangeCommit={handlePriceRangeCommit}
                        />
                    ) : null}

                    {filterData.product_parameters ? 
                        <>
                            {Object.keys(filterData.product_parameters).map((key, index) => {
                                const block = filterData.product_parameters[key];

                                const blockKey = `filter_block_${key}`;
                                const blockTitle = block?.settings?.label_txt;
                                const items = block.items ? Object.values(block.items) : [];
                                const selectedSet = selectedItemsByBlock[blockKey] || new Set();
                                const selectedCount = selectedSet.size;

                                return (
                                    <FilterBlock
                                        key={blockKey}
                                        title={blockTitle}
                                        selectedCount={selectedCount}
                                        items={items}
                                        selectedItems={selectedSet}
                                        isOpen={is_block_open(blockKey)}
                                        onToggle={() => toggle_filter_block(blockKey)}
                                        onItemClick={(item) => handleItemClick(blockKey, item)}
                                    />
                                );
                            })}
                        </>
                    : ''}
                    
                </div>
                <div className='filter_actions'>
                    <button
                        type='button'
                        className='filter_actions__button'
                        onClick={hide}
                        disabled={filterLoading.loading===1}
                    >
                        {i18n.t('productsFilterShowResults', { count: productCount })}
                    </button>
                </div>
            </div>
        </div>
    </SlideInContent>
    

});



export default ProuctsFilters;